import { KaraokeTrackManager } from '@src/editor/lib/KaraokeTrackManager';
import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import {
  IdGenerator,
  IdGeneratorEntityType,
} from '@src/editor/services/IdGenerator';
import { LogManager } from '@src/utility/Logger';

import { parseTextIntoTokens } from '../../services/textParsing';
import { AbstractCommand } from '../AbstractCommand';

import type { TranscriptTrack } from '../../models/TranscriptTrack';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  TranscriptPunctToken,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('transcript:multiReplace');

/**
 * In-place multi-segment replacement algorithm.
 * Constraint: DO NOT change number or ordering of segment elements.
 * We mutate only internal token arrays while preserving mediaStart/mediaEnd.
 * Steps:
 *  1. Identify contiguous segment range and capture deep snapshots for undo.
 *  2. Determine global replacement interval using first & last replaced tokens.
 *  3. Parse new text into word + punctuation tokens (simple splitter; punctuation has no timing).
 *  4. Uniformly distribute new word durations across interval (future improvement: proportional pacing).
 *  5. Assign each word to a segment based on its midpoint time ∈ [segment.mediaStart, segment.mediaEnd).
 *  6. Assign punctuation to segment of preceding word (or first segment if leading, fallback to last if needed).
 *  7. Rebuild segment token arrays:
 *     - First segment keeps head tokens before fromIndex then its assigned new tokens.
 *     - Interior segments replaced entirely by their assigned tokens.
 *     - Last segment gets its assigned new tokens then tail tokens after toIndex.
 *  8. Relative timestamps recalculated against owning segment start; existing segment media window untouched.
 *  9. New tokens marked state='added'; removed originals simply omitted.
 * 10. Undo restores original deep snapshots.
 * Complexity: O(R + W) (R = removed tokens count, W = new words).
 */
export class TranscriptMultiSegmentTextReplaceCommand extends AbstractCommand {
  private readonly fromSegmentId: string;
  private readonly fromIndex: number;
  private readonly toSegmentId: string;
  private readonly toIndex: number;
  private readonly newText: string;

  private originalElements: VideoTranscriptElement[] = [];
  private originalSnapshots: ClipTranscriptSegment[] = []; // deep copies for undo

  constructor(
    editor: VideoEditor,
    fromSegmentId: string,
    fromIndex: number,
    toSegmentId: string,
    toIndex: number,
    newText: string,
  ) {
    super(editor);
    this.fromSegmentId = fromSegmentId;
    this.fromIndex = fromIndex;
    this.toSegmentId = toSegmentId;
    this.toIndex = toIndex;
    this.newText = newText;
  }

  getName(): string {
    return 'TranscriptMultiSegmentTextReplaceCommand';
  }

  getDescription(): string {
    return `Replace multi-segment transcript text from (${this.fromSegmentId}:${this.fromIndex}) to (${this.toSegmentId}:${this.toIndex}) with "${this.newText}"`;
  }

  execute(): boolean {
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) {
      logger.error('No transcript track');
      return false;
    }

    if (!this.setOriginalElements(track)) return false;

    this.captureSnapshots();

    const segmentList = this.getSegmentList();

    const assigned = this.distributeReplacedTextAcrossSegmentList(segmentList);
    if (!assigned) return false;

    this.rebuildEachSegment(assigned, segmentList);

    this.editor.markDirty('transcript_multi_segment_text_replace');
    this.reconcileKaraokeTrack();
    return true;
  }

  undo(): boolean {
    if (!this.originalElements.length || !this.originalSnapshots.length)
      return false;
    this.originalElements.forEach((el, idx) => {
      el.setClipTranscriptSegment(this.originalSnapshots[idx]);
    });
    this.editor.markDirty('transcript_multi_segment_text_replace_undo');
    this.reconcileKaraokeTrack();
    return true;
  }

  private reconcileKaraokeTrack() {
    const karaokeTrack = this.editor.getKaraokeTrack();
    if (karaokeTrack) {
      new KaraokeTrackManager(this.editor).reconcileKaraokeTrack();
    }
  }

  private captureSnapshots() {
    this.originalSnapshots = this.originalElements.map(el => {
      const seg = el.getClipTranscriptSegment();
      return new ClipTranscriptSegment(
        [...seg.getWordsWithPunctuation()],
        seg.mediaStart,
        seg.mediaEnd,
      );
    });
  }

  private distributeReplacedTextAcrossSegmentList(
    segmentList: ClipTranscriptSegment[],
  ): Map<string, ClipTranscriptToken[]> | false {
    const firstSegment = this.originalElements[0].getClipTranscriptSegment();
    const lastSegment =
      this.originalElements[
        this.originalElements.length - 1
      ].getClipTranscriptSegment();
    const firstTokens = firstSegment.getWordsWithPunctuation();
    const lastTokens = lastSegment.getWordsWithPunctuation();
    const startToken = firstTokens[this.fromIndex];
    const endToken = lastTokens[this.toIndex];
    if (!startToken || !endToken) {
      logger.error('Token indexes out of bounds', {
        startToken,
        endToken,
      });
      return false;
    }
    const intervalStartMedia =
      startToken.type === 'text'
        ? startToken.media_ts ??
          firstSegment.mediaStart + startToken.relative_ts
        : firstSegment.mediaStart;
    const intervalEndMedia =
      endToken.type === 'text'
        ? endToken.media_end_ts ??
          lastSegment.mediaStart + endToken.relative_end_ts
        : lastSegment.mediaStart;
    const intervalDuration = Math.max(0, intervalEndMedia - intervalStartMedia);

    const parsedNew = parseTextIntoTokens(this.newText);
    const wordPieces = parsedNew.filter(t => t.type === 'text');
    const wordCount = wordPieces.length;
    const perWordDuration = wordCount > 0 ? intervalDuration / wordCount : 0;
    const generator = IdGenerator.get(
      IdGeneratorEntityType.TranscriptToken,
      'hex',
    );

    const assigned: Map<string, ClipTranscriptToken[]> = new Map(
      segmentList.map(s => [s.id, []]),
    );

    // Create word tokens and assign by midpoint
    wordPieces.forEach((w, idx) => {
      const mediaStart = intervalStartMedia + idx * perWordDuration;
      const mediaEnd = intervalStartMedia + (idx + 1) * perWordDuration;
      const midpoint = (mediaStart + mediaEnd) / 2;
      const owning =
        segmentList.find(
          s => midpoint >= s.mediaStart && midpoint < s.mediaEnd,
        ) || lastSegment;
      const token: ClipTranscriptTextToken = {
        id: generator.getNextIdHex(),
        type: 'text',
        value: w.value,
        media_ts: mediaStart,
        media_end_ts: mediaEnd,
        relative_ts: mediaStart - owning.mediaStart,
        relative_end_ts: mediaEnd - owning.mediaStart,
        initial_index: -1,
        state: 'added',
      };
      const bucket = assigned.get(owning.id);
      if (bucket) bucket.push(token);
    });

    // Assign punctuation tokens
    parsedNew.forEach((p, idx) => {
      if (p.type !== 'punct') return;
      // segment = segment of preceding word if exists, else first, else last if trailing
      let seg: ClipTranscriptSegment | undefined;
      for (let j = idx - 1; j >= 0; j--) {
        const prev = parsedNew[j];
        if (prev.type === 'text') {
          // find token
          seg = segmentList.find(s => {
            const list = assigned.get(s.id);
            return (
              !!list &&
              list.some(t => t.type === 'text' && t.value === prev.value)
            );
          });
          break;
        }
      }
      if (!seg) seg = segmentList[0];
      const punct: TranscriptPunctToken = {
        id: generator.getNextIdHex(),
        type: 'punct',
        value: p.value,
        initial_index: -1,
        state: 'added',
      };
      const punctBucket = assigned.get(seg.id);
      if (punctBucket) punctBucket.push(punct);
    });

    return assigned;
  }

  private rebuildEachSegment(
    assigned: Map<string, ClipTranscriptToken[]>,
    segmentList: ClipTranscriptSegment[],
  ): void {
    this.originalElements.forEach((el, idx) => {
      const seg = segmentList[idx];
      const tokens = seg.getWordsWithPunctuation();
      let newTokens: ClipTranscriptToken[] = [];
      if (idx === 0) {
        newTokens = [
          ...tokens.slice(0, this.fromIndex),
          ...(assigned.get(seg.id) || []),
        ];
      } else if (idx === segmentList.length - 1) {
        newTokens = [
          ...(assigned.get(seg.id) || []),
          ...tokens.slice(this.toIndex + 1),
        ];
      } else {
        newTokens = [...(assigned.get(seg.id) || [])];
      }
      const updated = new ClipTranscriptSegment(
        newTokens,
        seg.mediaStart,
        seg.mediaEnd,
      );
      el.setClipTranscriptSegment(updated);
    });
  }

  private setOriginalElements(track: TranscriptTrack): boolean {
    const segmentElements = track.elements;
    const fromSegmentIdx = segmentElements.findIndex(
      el => el.getClipTranscriptSegment().id === this.fromSegmentId,
    );
    const toSegmentIdx = segmentElements.findIndex(
      el => el.getClipTranscriptSegment().id === this.toSegmentId,
    );
    if (
      fromSegmentIdx === -1 ||
      toSegmentIdx === -1 ||
      toSegmentIdx < fromSegmentIdx
    ) {
      logger.error('Invalid segment range for multi replace', {
        fromSegmentIdx,
        toSegmentIdx,
      });
      return false;
    }
    if (fromSegmentIdx === toSegmentIdx) {
      logger.error('Single segment range passed to multi-segment command');
      return false;
    }

    this.originalElements = segmentElements.slice(
      fromSegmentIdx,
      toSegmentIdx + 1,
    );
    return true;
  }

  private getSegmentList(): ClipTranscriptSegment[] {
    return this.originalElements.map(el => el.getClipTranscriptSegment());
  }
}
